import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MessagesSidebar } from '../components/messages/MessagesSidebar';
import { ChatArea } from '../components/messages/ChatArea';
import { ProfileSidebar } from '../components/messages/ProfileSidebar';
import { DashboardNavbar } from '../components/layout/DashboardNavbar';
import { getSocket } from '../services/socket';
import {
  fetchConversations,
  fetchMessages,
  sendMessage,
  markAsRead,
  deleteConversations,
  archiveConversations,
  unarchiveConversations,
  startConversation,
  setSelectedChat,
  setActiveTab,
  clearMessages,
  addNewMessage,
  setMessagesRead,
  removeMessage,
  updateUnreadCount,
  setOnlineUsers,
  addOnlineUser,
  removeOnlineUser,
  setUserTyping,
  clearUserTyping,
  setNewConversationPlayer,
} from '../features/messaging/messagingSlice';

export const MessagesPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isPlayerRoute = location.pathname.startsWith('/player');

  const {
    conversations,
    messages,
    selectedChat,
    activeTab,
    loadingConversations,
    loadingMessages,
    sendingMessage,
    startingConversation,
    newConversationPlayer,
    onlineUsers,
    typingUsers,
  } = useSelector((state) => state.messaging);
  const currentUser = useSelector((state) => state.auth.user);
  const userRole = currentUser?.role || 'coach';

  const [showSidebar, setShowSidebar] = useState(true);
  const [showProfileSidebar, setShowProfileSidebar] = useState(false);

  // Fetch conversations on tab change
  useEffect(() => {
    dispatch(fetchConversations(activeTab));
  }, [activeTab, dispatch]);

  // Fetch messages when chat selected
  useEffect(() => {
    if (selectedChat?._id) {
      dispatch(fetchMessages({ conversationId: selectedChat._id }));
      dispatch(markAsRead(selectedChat._id));
      const socket = getSocket();
      if (socket) {
        socket.emit('joinChat', { chatId: selectedChat._id });
      }
    } else {
      dispatch(clearMessages());
    }
  }, [selectedChat?._id, dispatch]);

  // Socket listeners
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Request current online users (in case we missed the initial event)
    socket.emit('getOnlineUsers');

    const handleNewMessage = (data) => {
      dispatch(addNewMessage(data));
      // If viewing this conversation, mark as read
      if (data.conversationId === selectedChat?._id) {
        dispatch(markAsRead(data.conversationId));
      }
    };

    const handleMessageRead = ({ chatId }) => {
      dispatch(setMessagesRead(chatId));
    };

    const handleMessageDeleted = (data) => {
      dispatch(removeMessage(data));
    };

    const handleUnreadUpdate = (data) => {
      dispatch(updateUnreadCount(data));
    };

    const handleOnlineUsers = (users) => {
      dispatch(setOnlineUsers(users));
    };
    const handleUserOnline = ({ userId }) => {
      dispatch(addOnlineUser(userId));
    };
    const handleUserOffline = ({ userId }) => {
      dispatch(removeOnlineUser(userId));
    };
    const handleTyping = ({ chatId, userId }) => {
      dispatch(setUserTyping({ chatId, userId }));
    };
    const handleStopTyping = ({ chatId }) => {
      dispatch(clearUserTyping({ chatId }));
    };

    socket.on('newMessage', handleNewMessage);
    socket.on('messageRead', handleMessageRead);
    socket.on('messageDeleted', handleMessageDeleted);
    socket.on('unreadUpdate', handleUnreadUpdate);
    socket.on('onlineUsers', handleOnlineUsers);
    socket.on('userOnline', handleUserOnline);
    socket.on('userOffline', handleUserOffline);
    socket.on('typing', handleTyping);
    socket.on('stopTyping', handleStopTyping);

    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('messageRead', handleMessageRead);
      socket.off('messageDeleted', handleMessageDeleted);
      socket.off('unreadUpdate', handleUnreadUpdate);
      socket.off('onlineUsers', handleOnlineUsers);
      socket.off('userOnline', handleUserOnline);
      socket.off('userOffline', handleUserOffline);
      socket.off('typing', handleTyping);
      socket.off('stopTyping', handleStopTyping);
    };
  }, [dispatch, selectedChat?._id]);

  // Handle navigation from MessagesModal (only once)
  useEffect(() => {
    if (location.state?.selectedConversation) {
      // Use full conversation object from modal
      dispatch(setSelectedChat(location.state.selectedConversation));
      // Clear the state properly using navigate with replace
      navigate(location.pathname, { replace: true, state: {} });
    } else if (location.state?.selectedMessageId && conversations.length > 0) {
      // Fallback: find conversation from list by ID
      const convo = conversations.find(c => c._id === location.state.selectedMessageId);
      if (convo) {
        dispatch(setSelectedChat(convo));
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.selectedMessageId, location.state?.selectedConversation]);

  // Handle new conversation started from StatsTable message icon
  useEffect(() => {
    if (location.state?.newConversationPlayer) {
      handleStartNewConversation(location.state.newConversationPlayer);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.newConversationPlayer]);

  // Hide sidebar on mobile when chat is selected
  useEffect(() => {
    if (selectedChat && window.innerWidth < 768) {
      setShowSidebar(false);
    }
  }, [selectedChat]);

  const handleSelectChat = (chat) => {
    dispatch(setSelectedChat(chat));
    setShowProfileSidebar(false);
    if (window.innerWidth < 768) {
      setShowSidebar(false);
    }
  };

  const handleBackToMessages = () => {
    const socket = getSocket();
    if (socket && selectedChat?._id) {
      socket.emit('leaveChat', { chatId: selectedChat._id });
    }
    setShowSidebar(true);
    dispatch(setSelectedChat(null));
    setShowProfileSidebar(false);
  };

  const handleSendMessage = useCallback((text, files = []) => {
    if (!selectedChat?._id || (!text.trim() && files.length === 0)) return;
    dispatch(sendMessage({ conversationId: selectedChat._id, text: text.trim(), files }));
  }, [selectedChat?._id, dispatch]);

  const handleSendFirstMessage = useCallback((playerId, text, files = []) => {
    if (!playerId || (!text.trim() && files.length === 0)) return;
    dispatch(startConversation({ playerId, text: text.trim(), files }));
  }, [dispatch]);

  const handleDeleteConversations = (conversationIds) => {
    dispatch(deleteConversations(conversationIds));
  };

  const handleArchiveConversations = (conversationIds) => {
    dispatch(archiveConversations(conversationIds));
  };

  const handleUnarchiveConversations = (conversationIds) => {
    dispatch(unarchiveConversations(conversationIds));
  };

  const handleTabChange = (tab) => {
    dispatch(setActiveTab(tab));
  };

  const toggleProfileSidebar = () => {
    setShowProfileSidebar(!showProfileSidebar);
  };

  const handleStartNewConversation = (player) => {
    // Set the player for new conversation
    dispatch(setNewConversationPlayer(player));
    // Create a temporary selected chat to show chat area
    dispatch(setSelectedChat({
      _id: null, // No conversation ID yet
      isNew: true,
      participant: {
        _id: player._id,
        firstName: player.firstName,
        lastName: player.lastName,
        profileImage: player.profileImage
      }
    }));
    if (window.innerWidth < 768) {
      setShowSidebar(false);
    }
  };

  return (
    <>
      {isPlayerRoute && <DashboardNavbar userRole="player" />}
      <div className={`flex overflow-hidden bg-jp-page ${isPlayerRoute ? 'h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)] pt-16 sm:pt-20' : 'h-[calc(100vh-4rem)] sm:h-[calc(100vh-5rem)]'}`}>
        {/* Left Sidebar - Messages List */}
        <div className={`${showSidebar ? 'flex' : 'hidden md:flex'} flex-shrink-0`}>
          <MessagesSidebar
            selectedChat={selectedChat}
            onSelectChat={handleSelectChat}
            conversations={conversations}
            loading={loadingConversations}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            currentUserId={currentUser?._id}
            onDeleteConversations={handleDeleteConversations}
            onArchiveConversations={handleArchiveConversations}
            onUnarchiveConversations={handleUnarchiveConversations}
            onlineUsers={onlineUsers}
            userRole={userRole}
            onStartNewConversation={handleStartNewConversation}
          />
        </div>

        {/* Center - Chat Area */}
        <div className={`${!showSidebar || selectedChat ? 'flex' : 'hidden md:flex'} flex-1 min-w-0 relative`}>
          <ChatArea
            selectedChat={selectedChat}
            messages={messages}
            loading={loadingMessages}
            onBack={handleBackToMessages}
            onToggleProfile={toggleProfileSidebar}
            onSendMessage={handleSendMessage}
            onSendFirstMessage={handleSendFirstMessage}
            currentUserId={currentUser?._id}
            sendingMessage={sendingMessage}
            startingConversation={startingConversation}
            onlineUsers={onlineUsers}
            typingUsers={typingUsers}
          />

          {/* Mobile/Tablet Profile Sidebar Overlay */}
          {selectedChat && showProfileSidebar && (
            <>
              <div
                className="lg:hidden fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
                onClick={() => setShowProfileSidebar(false)}
              />
              <div className="lg:hidden fixed right-0 top-0 h-full w-80 bg-jp-card backdrop-blur-md border-l border-jp-line z-[70] overflow-y-auto">
                <div className="sticky top-0 bg-jp-card p-4 border-b border-jp-line flex items-center justify-between">
                  <h2 className="text-jp-ink font-semibold text-lg">Profile</h2>
                  <button
                    onClick={() => setShowProfileSidebar(false)}
                    className="p-2 hover:bg-jp-line rounded-lg transition-colors text-jp-ink"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <ProfileSidebar selectedChat={selectedChat} isMobileOverlay={true} onlineUsers={onlineUsers} />
              </div>
            </>
          )}
        </div>

        {/* Right Sidebar - Profile (Desktop) */}
        {selectedChat && <ProfileSidebar selectedChat={selectedChat} onlineUsers={onlineUsers} />}
      </div>
    </>
  );
};

export default MessagesPage;
